import { parseISO } from "date-fns";
import { useContext, useState } from "react";
import { Alert, Button, Card, Col, Container, ListGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  FaShieldAlt,
  FaRobot,
  FaLock,
  FaCloudUploadAlt,
  FaEnvelope,
  FaGoogle,
  FaForward,
  FaCog,
  FaMagic,
  FaMicrosoft,
  FaApple,
  FaYahoo,
  FaPiggyBank,
  FaReceipt,
  FaUpload,
} from "react-icons/fa";
import LanguageSwitcher from "../components/LanguageSwitcher";
import AppContext from "../context/app-context";
import { getWeeklyStats } from "../utils/dateHelpers";
import Footer from "../StaticPage/Footer";
import EmailSetUpComp from "../StaticPage/EmailSetUpComp";

export default function HomePage() {
  const { userProfile, trackingItems = [] } = useContext(AppContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { count: weeklyCount, total: weeklyTotal } = getWeeklyStats(trackingItems);
  const totalExpenses = trackingItems.length;
  const isMobile = window.innerWidth <= 768;

  return (
    <Container className="py-4">
      {isMobile && <LanguageSwitcher />}
      <HeroSection userProfile={userProfile} navigate={navigate} t={t} />
      {userProfile && (
        <WelcomeAlert
          t={t}
          userName={userProfile.displayName}
          totalExpenses={totalExpenses}
          weeklyCount={weeklyCount}
          weeklyTotal={weeklyTotal}
        />
      )}
      <FeatureGrid t={t} />

      {/* AI Technology Section */}
      <Row className="mb-5">
        <Col md={6} className="mb-4 mb-md-0">
          <div className="p-4 bg-light rounded-3 h-100">
            <FaRobot className="text-primary mb-3" size={40} />
            <h3>{t("ai.title")}</h3>
            <p>{t("ai.description")}</p>
            <ul className="ai-feature-list">
              <li>📸 {t("ai.feature1")}</li>
              <li>✅ {t("ai.feature2")}</li>
              <li>📅 {t("ai.feature3")}</li>
              <li>🗂️ {t("ai.feature4")}</li>
            </ul>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="p-3 p-md-4 bg-light rounded-3 h-100">
            <div className="ai-process-visual d-flex flex-column flex-md-row align-items-center justify-content-around">
              {/* Step 1 */}
              <div className="process-step d-flex flex-column align-items-center mb-3 mb-md-0">
                <div className="step-number">1</div>
                <FaCloudUploadAlt size={24} className="my-2" />
                <p className="text-center mb-0">{t("ai.step1")}</p>
              </div>

              {/* Arrow - becomes vertical on mobile */}
              <div className="process-arrow d-none d-md-block">→</div>
              <div className="process-arrow d-block d-md-none my-2">↓</div>

              {/* Step 2 */}
              <div className="process-step d-flex flex-column align-items-center mb-3 mb-md-0">
                <div className="step-number">2</div>
                <FaRobot size={24} className="my-2" />
                <p className="text-center mb-0">{t("ai.step2")}</p>
              </div>

              {/* Arrow - becomes vertical on mobile */}
              <div className="process-arrow d-none d-md-block">→</div>
              <div className="process-arrow d-block d-md-none my-2">↓</div>

              {/* Step 3 */}
              <div className="process-step d-flex flex-column align-items-center">
                <div className="step-number">3</div>
                <FaShieldAlt size={24} className="my-2" />
                <p className="text-center mb-0">{t("ai.step3")}</p>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      {/* Email Receipt Setup Section */}
      <EmailSetUpComp />
      {/* Security Section */}
      {/* <Card className="shadow-lg mb-5 border-0" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={4} className="text-center mb-4 mb-md-0">
              <FaShieldAlt className="text-primary" size={80} />
              <h3 className="mt-3">{t("security.title")}</h3>
            </Col>
            <Col md={8}>
              <div className="security-features">
                <div className="d-flex mb-3">
                  <FaLock className="text-success mt-1 me-3" size={24} />
                  <div>
                    <h5>{t("security.awsTitle")}</h5>
                    <p className="text-muted mb-0">{t("security.awsDesc")}</p>
                  </div>
                </div>
                <div className="d-flex mb-3">
                  <FaCloudUploadAlt className="text-info mt-1 me-3" size={24} />
                  <div>
                    <h5>{t("security.cloudTitle")}</h5>
                    <p className="text-muted mb-0">{t("security.cloudDesc")}</p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card> */}

      <Footer />
    </Container>
  );
}

const WelcomeAlert = ({ userName, totalExpenses, weeklyCount, weeklyTotal, t }) => (
  <Alert variant="success" className="mt-4">
    {t("home.welcome", { name: userName })} 🎉
    <div className="mt-2">
      {t("home.trackedTotal", { count: totalExpenses })}
      {weeklyCount > 0 && <span>, {t("home.trackedThisWeek", { count: weeklyCount, total: weeklyTotal.toFixed(2) })}</span>}
    </div>
  </Alert>
);

const FeatureGrid = ({ t }) => (
  <Row className="feature-grid">
    <FeatureCard
      icon={<FaEnvelope className="text-info" />}
      title={t("home.features.emailTitle")}
      text={t("home.features.emailText")}
    />
    <FeatureCard
      icon={<FaReceipt className="text-primary" />}
      title={t("home.features.snapTitle")}
      text={t("home.features.snapText")}
    />
    <FeatureCard
      icon={<FaPiggyBank className="text-success" />}
      title={t("home.features.budgetTitle")}
      text={t("home.features.budgetText")}
    />
    <FeatureCard
      icon={<span className="text-warning">📈</span>}
      title={t("home.features.reportTitle")}
      text={t("home.features.reportText")}
    />
  </Row>
);

const FeatureCard = ({ icon, title, text }) => (
  <Col md={3} className="mb-4" style={{ margin: "10px 0" }}>
    <Card className="feature-card h-100 shadow-hover border-0">
      <Card.Body className="p-md-4 p-3 d-flex flex-column align-items-center text-center">
        <div className="feature-icon-wrapper mb-4">
          <div className="feature-icon display-4 d-flex align-items-center justify-content-center">{icon}</div>
        </div>
        <h4 className="mb-3 fw-bold fs-5">{title}</h4>
        <p className="text-muted mb-0 flex-grow-1">{text}</p>
      </Card.Body>
    </Card>
  </Col>
);

const TutorialSteps = ({ t }) => (
  <Row className="tutorial-steps gy-4" style={{ padding: "40px 0" }}>
    <TutorialStep number={1} title={t("home.steps.1.title")} text={t("home.steps.1.text")} />
    <TutorialStep number={2} title={t("home.steps.2.title")} text={t("home.steps.2.text")} />
    <TutorialStep number={3} title={t("home.steps.3.title")} text={t("home.steps.3.text")} />
    <TutorialStep number={4} title={t("home.steps.4.title")} text={t("home.steps.4.text")} />
  </Row>
);

const TutorialStep = ({ number, title, text }) => (
  <Col md={3} className="h-100">
    <div className="h-100 d-flex flex-column align-items-center p-3">
      <div
        className="step-number mb-3 p-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
        style={{
          width: "60px",
          height: "60px",
          fontSize: "1.5rem",
        }}
      >
        {number}
      </div>
      <h5 className="mb-2 px-2 text-center fw-bold">{title}</h5>
      <small className="text-muted text-center px-2 mb-3">{text}</small>
    </div>
  </Col>
);

const HeroSection = ({ userProfile, navigate, t }) => (
  <Card className="hero-card">
    <Row className="align-items-center" style={{ padding: "0 15px" }}>
      <Col md={6}>
        <h1 className="display-4 mb-3">🐨 {t("home.hero.title")}</h1>
        <p className="lead text-muted">{t("home.hero.subtitle")}</p>
        <div className="cta-buttons d-flex gap-3">
          <Button variant="primary" size="lg" onClick={() => navigate("/upload")}>
            <FaUpload className="me-2" />
            {t("home.hero.uploadButton")}
          </Button>
          {/* <Button variant="outline-primary" size="lg" onClick={() => navigate("/about")}>
            {t("home.hero.learnMore")}
          </Button> */}
          <Button variant="outline-primary" size="lg" onClick={() => navigate("/aboutus")}>
            {t("home.hero.aboutus")}
          </Button>
        </div>
      </Col>
      <Col md={6} className="d-none d-md-block">
        <img src="/kola2.jpg" alt="Hamster mascot" className="hero-image" />
      </Col>
    </Row>
  </Card>
);
